import { NavigationMenu as BaseNavigationMenu } from '@base-ui/react/navigation-menu';
import type { ComponentProps } from 'react';
import { ChevronDownIcon } from '../lib/icons.tsx';
import { LAYER } from '../lib/layer.ts';
import { cx, styled } from '../lib/styled.tsx';
import { popupSurface } from '../lib/surfaces.ts';

/**
 * The popup animates between panels of different sizes, so its transition lists
 * width and height alongside opacity — `--popup-width` / `--popup-height` are
 * the measurements Base UI publishes for the panel currently on screen.
 */
const popup =
  `${popupSurface} ` +
  'relative h-[var(--popup-height)] w-[var(--popup-width)] origin-[var(--transform-origin)] ' +
  'transition-[opacity,transform,width,height] duration-200 ease-out-quint ' +
  'data-starting-style:scale-95 data-starting-style:opacity-0 ' +
  'data-ending-style:scale-95 data-ending-style:opacity-0';

/**
 * Panels slide in from the side the previous panel left towards, which is what
 * `data-activation-direction` records.
 */
const content =
  'h-full w-[calc(100vw-2.5rem)] p-2 sm:w-max sm:max-w-[26rem] ' +
  'transition-[opacity,translate] duration-200 ease-out-quint ' +
  'data-starting-style:opacity-0 data-ending-style:opacity-0 ' +
  'data-starting-style:data-[activation-direction=left]:-translate-x-1/2 ' +
  'data-starting-style:data-[activation-direction=right]:translate-x-1/2 ' +
  'data-ending-style:data-[activation-direction=left]:translate-x-1/2 ' +
  'data-ending-style:data-[activation-direction=right]:-translate-x-1/2';

/** A chevron by default, flipped while this item's panel is open. */
function Icon({ children, className, ...props }: ComponentProps<typeof BaseNavigationMenu.Icon>) {
  return (
    <BaseNavigationMenu.Icon
      className={cx(
        'transition-transform duration-200 ease-out-quint data-popup-open:rotate-180',
        className,
      )}
      {...props}
    >
      {children ?? <ChevronDownIcon className="size-3.5" />}
    </BaseNavigationMenu.Icon>
  );
}

/**
 * Site navigation with panels of links — the header menu of a marketing site,
 * not an application's action menu. Use {@link Menu} when the rows do things
 * rather than go places.
 */
export const NavigationMenu = {
  Root: styled(BaseNavigationMenu.Root, 'relative'),
  List: styled(BaseNavigationMenu.List, 'flex list-none items-center gap-1 p-0'),
  Item: styled(BaseNavigationMenu.Item, ''),
  Trigger: styled(
    BaseNavigationMenu.Trigger,
    'inline-flex h-9 items-center gap-1.5 rounded-control px-3 text-sm font-medium text-ink outline-none ' +
      'transition-colors duration-150 ease-out-quint ' +
      'hover:bg-surface-muted data-popup-open:bg-surface-muted',
  ),
  Icon,
  Content: styled(BaseNavigationMenu.Content, content),
  Link: styled(
    BaseNavigationMenu.Link,
    'block rounded-control p-2 text-sm text-ink no-underline outline-none hover:bg-surface-muted',
  ),
  Portal: BaseNavigationMenu.Portal,
  Backdrop: styled(BaseNavigationMenu.Backdrop, 'fixed inset-0'),
  Positioner: styled(BaseNavigationMenu.Positioner, `${LAYER.popup} outline-none`, {
    sideOffset: 8,
  }),
  Popup: styled(BaseNavigationMenu.Popup, popup),
  Arrow: styled(BaseNavigationMenu.Arrow, ''),
  /** Clips the panel that is sliding out while the next one slides in. */
  Viewport: styled(BaseNavigationMenu.Viewport, 'relative size-full overflow-hidden'),
};
